import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface SchedulerMatch {
  id: string;
  leagueId?: string;
  homeTeamId?: string;
  awayTeamId?: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  fieldId?: string;
  status: string;
  homeTeam?: { id: string; name: string };
  awayTeam?: { id: string; name: string };
}

interface FieldInfo {
  id: string;
  name: string;
}

const TIME_SLOTS = [
  '08:00', '09:30', '11:00', '12:30', '14:00', '15:30', '17:00', '18:30', '20:00',
];

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const LEAGUE_COLORS = [
  '#1976D2', '#388E3C', '#F57C00', '#7B1FA2', '#C62828', '#00838F', '#4E342E', '#455A64',
];

const AdminScheduler: React.FC = () => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<SchedulerMatch[]>([]);
  const [fields, setFields] = useState<FieldInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'calendar' | 'timeline'>('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dragMatch, setDragMatch] = useState<SchedulerMatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [leagues, setLeagues] = useState<Array<{ id: string; name: string }>>([]);
  const [filterLeagueId, setFilterLeagueId] = useState<string>('');
  const [rescheduleMatchId, setRescheduleMatchId] = useState<string | null>(null);
  const [rescheduleForm, setRescheduleForm] = useState({ date: '', time: '', fieldId: '' });

  // Get start and end of current month for fetching
  const getMonthRange = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, [currentDate]);

  const fetchData = useCallback(async () => {
    try {
      const { startDate, endDate } = getMonthRange();
      const params: any = { startDate, endDate };
      if (filterLeagueId) params.leagueId = filterLeagueId;

      const response = await api.get('/matches/scheduler', { params });
      setMatches(response.data.matches || []);
      setFields(response.data.fields || []);
    } catch (err) {
      console.error('Error fetching scheduler data:', err);
    } finally {
      setLoading(false);
    }
  }, [getMonthRange, filterLeagueId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const response = await api.get('/leagues');
        setLeagues(response.data.leagues || response.data || []);
      } catch (err) {
        console.error('Error fetching leagues:', err);
      }
    };
    fetchLeagues();
  }, []);

  // Get unique league IDs for color mapping
  const leagueColorMap = new Map<string, string>();
  const uniqueLeagueIds = Array.from(new Set(matches.map((m) => m.leagueId).filter(Boolean))) as string[];
  uniqueLeagueIds.forEach((lid, idx) => {
    leagueColorMap.set(lid!, LEAGUE_COLORS[idx % LEAGUE_COLORS.length]);
  });

  const getMatchColor = (match: SchedulerMatch) => {
    return leagueColorMap.get(match.leagueId || '') || '#757575';
  };

  const getMatchLabel = (match: SchedulerMatch) => {
    const home = match.homeTeam?.name || 'TBD';
    const away = match.awayTeam?.name || 'TBD';
    return `${home} vs ${away}`;
  };

  const handleReschedule = async (matchId: string, newDate?: string, newTime?: string, newFieldId?: string) => {
    setError(null);
    try {
      const body: any = {};
      if (newDate) body.scheduledDate = newDate;
      if (newTime) body.scheduledTime = newTime;
      if (newFieldId) body.fieldId = newFieldId;

      await api.patch(`/matches/${matchId}/reschedule`, body);
      fetchData();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to reschedule';
      setError(msg);
    }
  };

  // Drag handlers
  const handleDragStart = (match: SchedulerMatch) => {
    setDragMatch(match);
  };

  const handleDrop = (date: string, time: string, fieldId: string) => {
    if (!dragMatch) return;
    handleReschedule(dragMatch.id, date, time, fieldId);
    setDragMatch(null);
  };

  // Navigate months
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Calendar grid generation
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);

    return days;
  };

  const getMatchesForDate = (dateStr: string) => {
    return matches.filter((m) => m.scheduledDate === dateStr);
  };

  const formatDateStr = (day: number) => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-${String(day).padStart(2, '0')}`;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Admin Scheduler</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={filterLeagueId}
            onChange={(e) => setFilterLeagueId(e.target.value)}
            style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
          >
            <option value="">All Leagues</option>
            {leagues.map((l: any) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          <button
            className={viewMode === 'calendar' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setViewMode('calendar')}
          >
            Calendar
          </button>
          <button
            className={viewMode === 'timeline' ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setViewMode('timeline')}
          >
            Timeline
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#FFEBEE', padding: '10px 15px', borderRadius: '8px', marginBottom: '15px', color: '#C62828' }}>
          {error}
          <button
            style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#757575' }}
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Month navigation */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
        <button className="btn btn-secondary" onClick={prevMonth}>&lt;</button>
        <h2 style={{ margin: 0 }}>
          {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <button className="btn btn-secondary" onClick={nextMonth}>&gt;</button>
      </div>

      {viewMode === 'calendar' && (
        <div>
          {/* Calendar grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '1px',
            background: '#E0E0E0',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            overflow: 'hidden',
          }}>
            {/* Day headers */}
            {DAYS_OF_WEEK.map((day) => (
              <div
                key={day}
                style={{
                  background: '#F5F5F5',
                  padding: '8px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                {day}
              </div>
            ))}

            {/* Calendar days */}
            {generateCalendarDays().map((day, idx) => {
              if (day === null) {
                return <div key={`empty-${idx}`} style={{ background: '#FAFAFA', minHeight: '100px' }} />;
              }

              const dateStr = formatDateStr(day);
              const dayMatches = getMatchesForDate(dateStr);
              const isSelected = selectedDate === dateStr;

              return (
                <div
                  key={dateStr}
                  onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragMatch) {
                      handleReschedule(dragMatch.id, dateStr);
                      setDragMatch(null);
                    }
                  }}
                  style={{
                    background: isSelected ? '#E3F2FD' : 'white',
                    minHeight: '100px',
                    padding: '5px',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>{day}</div>
                  {dayMatches.slice(0, 3).map((match) => (
                    <div
                      key={match.id}
                      draggable
                      onDragStart={() => handleDragStart(match)}
                      style={{
                        background: getMatchColor(match),
                        color: 'white',
                        fontSize: '11px',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        marginBottom: '2px',
                        cursor: 'grab',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                      title={`${match.scheduledTime} - ${getMatchLabel(match)}`}
                    >
                      {match.scheduledTime} {match.homeTeam?.name?.substring(0, 8)} vs {match.awayTeam?.name?.substring(0, 8)}
                    </div>
                  ))}
                  {dayMatches.length > 3 && (
                    <div style={{ fontSize: '11px', color: '#757575' }}>
                      +{dayMatches.length - 3} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected day detail */}
          {selectedDate && (
            <div className="card" style={{ marginTop: '20px' }}>
              <h3>Matches on {selectedDate}</h3>
              {getMatchesForDate(selectedDate).length === 0 ? (
                <p style={{ color: '#757575' }}>No matches scheduled.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Home</th>
                      <th>Away</th>
                      <th>Field</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getMatchesForDate(selectedDate).map((match) => (
                      <React.Fragment key={match.id}>
                        <tr>
                          <td>{match.scheduledTime}</td>
                          <td>{match.homeTeam?.name || 'TBD'}</td>
                          <td>{match.awayTeam?.name || 'TBD'}</td>
                          <td>{fields.find(f => f.id === match.fieldId)?.name || match.location || '-'}</td>
                          <td>
                            <span className={`badge ${match.status === 'completed' ? 'badge-success' : match.status === 'cancelled' ? 'badge-danger' : 'badge-info'}`}>
                              {match.status}
                            </span>
                          </td>
                          <td>
                            {match.status !== 'completed' && match.status !== 'cancelled' && (
                              <button
                                className="btn btn-secondary"
                                style={{ fontSize: '12px', padding: '5px 10px' }}
                                onClick={() => {
                                  if (rescheduleMatchId === match.id) {
                                    setRescheduleMatchId(null);
                                  } else {
                                    setRescheduleMatchId(match.id);
                                    setRescheduleForm({
                                      date: match.scheduledDate,
                                      time: match.scheduledTime,
                                      fieldId: match.fieldId || ''
                                    });
                                  }
                                }}
                              >
                                {rescheduleMatchId === match.id ? 'Cancel' : 'Reschedule'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {rescheduleMatchId === match.id && (
                          <tr>
                            <td colSpan={6} style={{ background: '#F5F5F5', padding: '15px' }}>
                              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label style={{ fontSize: '13px' }}>Date</label>
                                  <input
                                    type="date"
                                    value={rescheduleForm.date}
                                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, date: e.target.value })}
                                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                                  />
                                </div>
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label style={{ fontSize: '13px' }}>Time</label>
                                  <select
                                    value={rescheduleForm.time}
                                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, time: e.target.value })}
                                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                                  >
                                    {TIME_SLOTS.map(slot => (
                                      <option key={slot} value={slot}>{slot}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label style={{ fontSize: '13px' }}>Field</label>
                                  <select
                                    value={rescheduleForm.fieldId}
                                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, fieldId: e.target.value })}
                                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                                  >
                                    <option value="">No field</option>
                                    {fields.map(f => (
                                      <option key={f.id} value={f.id}>{f.name}</option>
                                    ))}
                                  </select>
                                </div>
                                <button
                                  className="btn btn-primary"
                                  style={{ fontSize: '13px', padding: '8px 16px' }}
                                  onClick={() => {
                                    const newDate = rescheduleForm.date !== match.scheduledDate ? rescheduleForm.date : undefined;
                                    const newTime = rescheduleForm.time !== match.scheduledTime ? rescheduleForm.time : undefined;
                                    const newFieldId = rescheduleForm.fieldId !== (match.fieldId || '') ? rescheduleForm.fieldId : undefined;
                                    if (newDate || newTime || newFieldId !== undefined) {
                                      handleReschedule(match.id, newDate, newTime, newFieldId || undefined);
                                    }
                                    setRescheduleMatchId(null);
                                  }}
                                >
                                  Save
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {viewMode === 'timeline' && (
        <div>
          {/* Date selector for timeline view */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ marginRight: '10px' }}>Select Date:</label>
            <input
              type="date"
              value={selectedDate || formatDateStr(new Date().getDate())}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
            />
          </div>

          {/* Timeline grid: fields as rows, time slots as columns */}
          <div style={{ overflowX: 'auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `150px repeat(${TIME_SLOTS.length}, minmax(120px, 1fr))`,
              gap: '1px',
              background: '#E0E0E0',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              overflow: 'hidden',
              minWidth: `${150 + TIME_SLOTS.length * 120}px`,
            }}>
              {/* Header row */}
              <div style={{ background: '#F5F5F5', padding: '10px', fontWeight: 'bold' }}>
                Field / Time
              </div>
              {TIME_SLOTS.map((slot) => (
                <div key={slot} style={{ background: '#F5F5F5', padding: '10px', textAlign: 'center', fontWeight: 'bold' }}>
                  {slot}
                </div>
              ))}

              {/* Field rows */}
              {fields.map((field) => (
                <React.Fragment key={field.id}>
                  <div style={{ background: 'white', padding: '10px', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                    {field.name}
                  </div>
                  {TIME_SLOTS.map((slot) => {
                    const dateStr = selectedDate || formatDateStr(new Date().getDate());
                    const slotMatches = matches.filter(
                      (m) => m.fieldId === field.id && m.scheduledDate === dateStr && m.scheduledTime === slot
                    );

                    return (
                      <div
                        key={`${field.id}-${slot}`}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleDrop(dateStr, slot, field.id)}
                        style={{
                          background: slotMatches.length > 0 ? '#F5F5F5' : 'white',
                          padding: '5px',
                          minHeight: '60px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '3px',
                        }}
                      >
                        {slotMatches.map((match) => (
                          <div
                            key={match.id}
                            draggable
                            onDragStart={() => handleDragStart(match)}
                            style={{
                              background: getMatchColor(match),
                              color: 'white',
                              padding: '5px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'grab',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={getMatchLabel(match)}
                          >
                            {match.homeTeam?.name?.substring(0, 10)} vs {match.awayTeam?.name?.substring(0, 10)}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}

              {fields.length === 0 && (
                <div style={{ gridColumn: `1 / span ${TIME_SLOTS.length + 1}`, padding: '20px', textAlign: 'center', background: 'white', color: '#757575' }}>
                  No fields configured. Add fields in the Fields section to use the timeline view.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      {uniqueLeagueIds.length > 0 && (
        <div style={{ marginTop: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <strong>Leagues:</strong>
          {uniqueLeagueIds.map((lid) => {
            const league = leagues.find((l: any) => l.id === lid);
            return (
              <div key={lid!} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: leagueColorMap.get(lid!) }} />
                <span style={{ fontSize: '13px' }}>{league?.name || lid}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminScheduler;
